// src/components/Stripe/SubscriptionPage.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Paper,
  Chip,
  useTheme,
  alpha,
  Fade,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '../config';

export default function SubscriptionPage() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();
  const theme = useTheme();

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [paymentRetryLoading, setPaymentRetryLoading] = useState(false);
  const [paymentUpdateLoading, setPaymentUpdateLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [promoAccess, setPromoAccess] = useState(null);
  const [checkoutPriceId, setCheckoutPriceId] = useState(null);

  // 1) Load current subscription & available plans
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      }),
      axios.get(`${API}/api/subscription/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    ])
      .then(([dash, plansResp]) => {
        setSubscription(dash.data.subscription);
        setPromoAccess(dash.data.promoAccess || null);
        setPlans(plansResp.data.plans || []);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load subscription data.');
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  // Check if direct payment is enabled via environment variable
  const enableDirectPayment = process.env.REACT_APP_ENABLE_DIRECT_PAYMENT === 'true';
  const [paymentMethod, setPaymentMethod] = useState('redirect'); // 'redirect' or 'direct'
  
  // Billing interval state for toggle
  const [billingInterval, setBillingInterval] = useState('month'); // 'month' or 'year'
  const plansSectionRef = useRef(null);
  
  // Check if yearly is available
  const yearlyPlan = plans.find(p => p.interval === 'year');
  const yearlyAvailable = !!yearlyPlan;
  
  // Filter plans based on selected billing interval
  const filteredPlans = plans.filter(plan => plan.interval === billingInterval);
  
  // If yearly is not available, default to monthly
  useEffect(() => {
    if (!yearlyAvailable && billingInterval === 'year') {
      setBillingInterval('month');
    }
  }, [yearlyAvailable, billingInterval]);
  
  // Calculate savings percentage for yearly plan
  const calculateSavings = () => {
    const monthlyPlan = plans.find(p => p.interval === 'month');
    const yearlyPlan = plans.find(p => p.interval === 'year');
    
    if (monthlyPlan && yearlyPlan) {
      const monthlyYearlyPrice = monthlyPlan.unitAmount * 12;
      const yearlyPrice = yearlyPlan.unitAmount;
      const savings = ((monthlyYearlyPrice - yearlyPrice) / monthlyYearlyPrice) * 100;
      return Math.round(savings);
    }
    return 20; // Default fallback
  };
  
  const savingsPercent = calculateSavings();

  const subscriptionStatus = subscription?.status || '';
  const hasManagedSubscription = ['active', 'cancel_at_period_end', 'past_due', 'promo_active'].includes(subscriptionStatus);
  const cancelledSubscription = subscriptionStatus === 'canceled' || Boolean(subscription?.canceled_at);
  const currentPeriodEnd = subscription?.current_period_end
    ? new Date(typeof subscription.current_period_end === 'number'
      ? subscription.current_period_end * 1000
      : subscription.current_period_end)
    : null;

  const heroTitle = cancelledSubscription ? 'Your subscription ended' : 'Ready to go VIP?';

  const startCheckout = async priceId => {
    setCheckoutPriceId(priceId);
    try {
      if (paymentMethod === 'redirect') {
        // Use Stripe Checkout redirect flow
        const { data } = await axios.post(
          `${API}/api/subscription/create-subscription`,
          { priceId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = data.checkoutUrl;
      } else {
        // Use direct payment flow with our custom UI
        navigate(`/payment?price_id=${priceId}`);
      }
    } catch {
      setError('Could not start checkout.');
      setCheckoutPriceId(null);
    }
  };

  const redeemPromoCode = async () => {
    const normalizedCode = promoCode.trim().toUpperCase();
    if (!normalizedCode) {
      setPromoError('Enter your promo code.');
      return;
    }
    setPromoLoading(true);
    setPromoError('');
    setPromoSuccess('');
    try {
      const { data } = await axios.post(
        `${API}/api/subscription/redeem-promo`,
        { code: normalizedCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription({
        status: 'promo_active',
        plan: `${data.durationDays}-Day Promo Access`,
        current_period_end: data.endsAt,
        isActive: true,
      });
      setPromoAccess({ active: true, redeemed: true, endsAt: data.endsAt, code: normalizedCode });
      setPromoSuccess(data.message);
      setPromoCode('');
    } catch (err) {
      setPromoError(err.response?.data?.error || 'Could not apply that promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    setCancellingSubscription(true);
    try {
      await axios.post(
        `${API}/api/subscription/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(prev => ({ ...prev, status: 'cancel_at_period_end' }));
      setCancelDialogOpen(false);
    } catch {
      setError('Failed to cancel subscription.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const handleUpdatePaymentMethod = async () => {
    setPaymentUpdateLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/subscription/update-payment-method`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = data.url;
    } catch (error) {
      setError('Failed to open payment update page.');
    } finally {
      setPaymentUpdateLoading(false);
    }
  };

  const handleRetryPayment = async () => {
    setPaymentRetryLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/subscription/retry-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (data.success) {
        // Payment succeeded, refresh subscription data
        setSubscription(prev => ({ ...prev, status: 'active' }));
        setError('');
      } else {
        setError(data.message || 'Payment failed. Please update your payment method.');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to retry payment.';
      setError(message);
    } finally {
      setPaymentRetryLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
          Loading subscription data...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert 
          severity="error" 
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Manage Your Subscription
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          View your current plan details and explore available subscription options
        </Typography>
      </Box>

      <Stack spacing={6}>
        {subscriptionStatus === 'promo_active' ? (
          <Stack spacing={3}>
            <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
              Your free VIP access is active until{' '}
              {subscription?.current_period_end
                ? new Date(subscription.current_period_end).toLocaleString()
                : 'the end of your promo period'}. No credit card is on file and you will not be charged.
            </Alert>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)}, ${alpha(theme.palette.secondary.main, 0.07)})`,
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Want to keep VIP without interruption?
                  </Typography>
                  <Typography color="text.secondary">
                    Choose a paid plan now to keep everything unlocked. Your paid subscription starts today and replaces the free preview.
                  </Typography>
                </Grid>
                <Grid item xs={12} md="auto">
                  <Stack spacing={1.5} sx={{ minWidth: { md: 230 } }}>
                    {plans.map((plan) => (
                      <Button
                        key={plan.priceId}
                        variant="contained"
                        size="large"
                        startIcon={checkoutPriceId === plan.priceId ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
                        disabled={Boolean(checkoutPriceId)}
                        onClick={() => startCheckout(plan.priceId)}
                        sx={{ py: 1.25, textTransform: 'none', fontWeight: 800 }}
                      >
                        {checkoutPriceId === plan.priceId
                          ? 'Opening checkout...'
                          : `Start paid VIP — $${(plan.unitAmount / 100).toFixed(2)}/${plan.interval === 'year' ? 'year' : 'month'}`}
                      </Button>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        ) : !hasManagedSubscription && !promoAccess?.redeemed ? (
          <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Have a free-access code?
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Unlock VIP for seven days without entering a credit card. Access ends automatically unless you choose a paid plan.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Promo code"
                value={promoCode}
                onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') redeemPromoCode();
                }}
                inputProps={{ maxLength: 32 }}
                error={Boolean(promoError)}
                helperText={promoError || 'One free-access promo may be used per account.'}
              />
              <Button
                variant="contained"
                onClick={redeemPromoCode}
                disabled={promoLoading}
                sx={{ minWidth: 170, alignSelf: { sm: 'flex-start' }, height: 56 }}
              >
                {promoLoading ? <CircularProgress size={24} color="inherit" /> : 'Unlock free access'}
              </Button>
            </Box>
            {promoSuccess && <Alert severity="success" sx={{ mt: 2 }}>{promoSuccess}</Alert>}
          </Paper>
        ) : !hasManagedSubscription && promoAccess?.redeemed ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Your free-access period has ended. Choose a paid plan below to unlock VIP again.
          </Alert>
        ) : null}

        {/* Current Subscription */}
        <Fade in timeout={500}>
          <Paper
            elevation={hasManagedSubscription ? 2 : 3}
            sx={{
              p: { xs: 4, md: hasManagedSubscription ? 4 : 5 },
              borderRadius: 3,
              background: hasManagedSubscription
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`
                : theme.palette.background.paper,
              border: hasManagedSubscription
                ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
                : `1px dashed ${alpha(theme.palette.primary.main, 0.22)}`,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: hasManagedSubscription ? undefined : '0 24px 60px rgba(16, 35, 80, 0.12)',
              ...(hasManagedSubscription && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 6,
                  height: '100%',
                  backgroundColor:
                    subscriptionStatus === 'cancel_at_period_end'
                      ? '#d32f2f'
                      : subscriptionStatus === 'past_due'
                      ? theme.palette.warning.main
                      : theme.palette.success.main,
                },
              }),
            }}
          >
            {hasManagedSubscription ? (
            <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: subscription?.status === 'active' 
                    ? alpha(theme.palette.success.main, 0.1)
                    : subscription?.status === 'cancel_at_period_end'
                    ? alpha('#d32f2f', 0.1)
                    : subscription?.status === 'past_due'
                    ? alpha(theme.palette.warning.main, 0.1)
                    : alpha(theme.palette.grey[500], 0.1),
                  mr: 3,
                  border: `2px solid ${
                    subscription?.status === 'active' 
                      ? theme.palette.success.main 
                      : subscription?.status === 'cancel_at_period_end'
                      ? '#d32f2f'
                      : subscription?.status === 'past_due'
                      ? theme.palette.warning.main
                      : theme.palette.grey[500]
                  }`,
                }}
              >
                {['active', 'promo_active'].includes(subscription?.status) ? (
                  <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: '1.5rem' }} />
                ) : subscription?.status === 'past_due' ? (
                  <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: '1.5rem' }} />
                ) : (
                  <CancelIcon sx={{ color: theme.palette.grey[500], fontSize: '1.5rem' }} />
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                  Current Plan
                </Typography>
                {subscription?.status === 'active' && (
                  <Chip 
                    label="Active" 
                    color="success" 
                    size="small" 
                    sx={{ 
                      fontWeight: 600,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  />
                )}
                {subscription?.status === 'promo_active' && (
                  <Chip label="Free Promo Active" color="success" size="small" sx={{ fontWeight: 600 }} />
                )}
                {subscription?.status === 'cancel_at_period_end' && (
                  <Chip 
                    label="Cancelled - Active Until Expiry" 
                    sx={{ 
                      fontWeight: 600,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      border: '1px solid #e57373',
                    }}
                  />
                )}
                {subscription?.status === 'past_due' && (
                  <Chip 
                    label="Payment Failed - Action Required" 
                    sx={{ 
                      fontWeight: 600,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.dark,
                      border: `1px solid ${theme.palette.warning.main}`,
                    }}
                  />
                )}
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Payment Failed Alert for past_due status */}
            {subscription?.status === 'past_due' && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 4,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.warning.main}`,
                  backgroundColor: alpha(theme.palette.warning.main, 0.05),
                }}
                action={
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button 
                      size="small" 
                      variant="contained"
                      color="warning"
                      startIcon={paymentUpdateLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
                      onClick={handleUpdatePaymentMethod}
                      disabled={paymentUpdateLoading}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                      }}
                    >
                      {paymentUpdateLoading ? 'Opening...' : 'Update Payment'}
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined"
                      color="warning"
                      startIcon={paymentRetryLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                      onClick={handleRetryPayment}
                      disabled={paymentRetryLoading}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                      }}
                    >
                      {paymentRetryLoading ? 'Retrying...' : 'Retry Payment'}
                    </Button>
                  </Box>
                }
              >
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Payment Failed - Your subscription is at risk
                  </Typography>
                  <Typography variant="body2">
                    Your recent payment couldn't be processed. Please update your payment method or retry the payment to keep your subscription active.
                  </Typography>
                </Box>
              </Alert>
            )}
            
            {(subscription?.status === 'active' || subscription?.status === 'cancel_at_period_end' || subscription?.status === 'past_due' || subscription?.status === 'promo_active') ? (
              <Box sx={{ mt: 4 }}>
                {/* Main Subscription Card */}
                <Card 
                  elevation={0}
                  sx={{
                    background: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    mb: 3,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  }}
                >
                  {/* Premium Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderBottomLeftRadius: 20,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    Premium
                  </Box>
                  
                  <CardContent sx={{ p: 4, pb: 3 }}>
                    <Grid container spacing={4}>
                      {/* Plan Information */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography 
                            variant="h4" 
                            fontWeight={800}
                            sx={{ 
                              mb: 1,
                              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                              backgroundClip: 'text',
                              textFillColor: 'transparent',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontSize: { xs: '1.5rem', md: '2rem' },
                            }}
                          >
                            {subscription.plan || 'OnlyStats VIP'}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {subscription.status === 'promo_active'
                              ? 'Full VIP access with no credit card and no automatic charge'
                              : 'Your premium subscription with full access to all features'}
                          </Typography>
                        </Box>
                        
                        {/* Billing Information */}
                        <Box 
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: theme.palette.primary.main,
                              fontSize: '1.2rem',
                              fontWeight: 800,
                            }}
                          >
                            $
                          </Box>
                          <Box>
                            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                              {subscription.status === 'promo_active' ? 'Free' : subscription.amount && subscription.currency ?
                                `${(subscription.amount / 100).toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: subscription.currency.toUpperCase(),
                                  minimumFractionDigits: 0
                                })}` : 
                                plans.find(p => p.interval === 'month') 
                                  ? `$${(plans.find(p => p.interval === 'month').unitAmount / 100).toFixed(2)}`
                                  : '$1.00'
                              }
                              {subscription.status !== 'promo_active' && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                  per {subscription.interval || 'month'}
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {subscription.status === 'promo_active'
                                ? 'No payment method required'
                                : `Billed ${subscription.interval === 'year' ? 'annually' : 'monthly'}`}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      {/* Status and Date Information */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          {/* Status */}
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                              Status
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {subscription.status === 'active' ? (
                                <>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: theme.palette.success.main,
                                      boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.2)}`,
                                    }}
                                  />
                                  <Typography variant="h6" fontWeight={700} color="success.main">
                                    Active Subscription
                                  </Typography>
                                </>
                              ) : subscription.status === 'promo_active' ? (
                                <>
                                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.success.main, boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.2)}` }} />
                                  <Typography variant="h6" fontWeight={700} color="success.main">
                                    Free VIP Access
                                  </Typography>
                                </>
                              ) : subscription.status === 'cancel_at_period_end' ? (
                                <>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: '#d32f2f',
                                      boxShadow: `0 0 0 4px ${alpha('#d32f2f', 0.2)}`,
                                    }}
                                  />
                                  <Typography variant="h6" fontWeight={700} sx={{ color: '#c62828' }}>
                                    Cancelled: Expires on Renewal Date
                                  </Typography>
                                </>
                              ) : subscription.status === 'past_due' ? (
                                <>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: theme.palette.warning.main,
                                      boxShadow: `0 0 0 4px ${alpha(theme.palette.warning.main, 0.2)}`,
                                    }}
                                  />
                                  <Typography variant="h6" fontWeight={700} color="warning.main">
                                    Payment Failed - Action Required
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: theme.palette.error.main,
                                      boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.2)}`,
                                    }}
                                  />
                                  <Typography variant="h6" fontWeight={700} color="error.main">
                                    {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                                  </Typography>
                                </>
                              )}
                            </Box>
                            {subscription.status === 'cancel_at_period_end' && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 5 }}>
                                Your subscription will not renew automatically
                              </Typography>
                            )}
                            {subscription.status === 'past_due' && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 5 }}>
                                Update your payment method to avoid service interruption
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Date Information */}
                          <Box
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              backgroundColor: subscription.status === 'cancel_at_period_end' 
                                ? alpha(theme.palette.warning.main, 0.04)
                                : alpha(theme.palette.info.main, 0.04),
                              border: `1px solid ${alpha(
                                subscription.status === 'cancel_at_period_end' 
                                  ? theme.palette.warning.main 
                                  : theme.palette.info.main, 
                                0.08
                              )}`,
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                            >
                              {['cancel_at_period_end', 'promo_active'].includes(subscription.status) ? 'Expires On' : 'Renews On'}
                            </Typography>
                            <Typography 
                              variant="h5" 
                              fontWeight={700}
                              color={subscription.status === 'cancel_at_period_end' ? 'warning.main' : 'info.main'}
                              sx={{ mb: 1 }}
                            >
                              {currentPeriodEnd ?
                                currentPeriodEnd.toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                }) : 
                                'Date not available'
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {currentPeriodEnd ?
                                currentPeriodEnd.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                }) : 
                                ''
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Box sx={{ py: 4 }}>
                {/* Show win-back message for canceled subscriptions with history */}
                {subscription && (subscription.status === 'canceled' || subscription.canceled_at) ? (
                  <Box>
                    {/* Header */}
                    <Box 
                      sx={{
                        textAlign: 'center',
                        p: 4,
                        pb: 3,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                        position: 'relative',
                        overflow: 'hidden',
                        mb: 4,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -20,
                          right: -20,
                          width: 100,
                          height: 100,
                          borderRadius: '50%',
                          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                          filter: 'blur(20px)',
                        }}
                      />
                      
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Box
                          sx={{
                            fontSize: '3rem',
                            mb: 2,
                            display: 'inline-block',
                            opacity: 0.8,
                          }}
                        >
                          💔
                        </Box>
                        
                        <Typography 
                          variant="h4" 
                          fontWeight={700}
                          gutterBottom
                          sx={{
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 2,
                          }}
                        >
                          We Miss You!
                        </Typography>
                        
                        <Typography 
                          variant="body1" 
                          color="text.secondary"
                          sx={{ mb: 1, maxWidth: 500, mx: 'auto' }}
                        >
                          Ready to rejoin? Choose a plan below.
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Billing Interval Toggle */}
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                      }}
                    >
                      <ToggleButtonGroup
                        value={billingInterval}
                        exclusive
                        onChange={(e, newInterval) => {
                          if (newInterval !== null) {
                            setBillingInterval(newInterval);
                          }
                        }}
                        aria-label="billing interval"
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 3,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          '& .MuiToggleButton-root': {
                            px: 4,
                            py: 1.5,
                            border: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            textTransform: 'none',
                            color: theme.palette.text.secondary,
                            transition: 'all 0.3s ease',
                            '&.Mui-selected': {
                              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                              color: 'white',
                              '&:hover': {
                                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                              },
                            },
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          },
                        }}
                      >
                        <ToggleButton value="month" aria-label="monthly billing">
                          Monthly
                        </ToggleButton>
                        {yearlyAvailable && (
                        <ToggleButton 
                          value="year" 
                          aria-label="yearly billing"
                          sx={{ position: 'relative' }}
                        >
                          Yearly
                          {savingsPercent > 0 && (
                            <Chip
                              label={`Save ${savingsPercent}%`}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                color: 'white',
                                border: `1px solid ${theme.palette.success.light}`,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                            />
                          )}
                        </ToggleButton>
                        )}
                      </ToggleButtonGroup>
                    </Box>
                    
                    {/* Plans */}
                    <Grid container spacing={3} justifyContent="center">
                      {filteredPlans.map((plan, index) => {
                        const price = (plan.unitAmount / 100).toLocaleString('en-US', {
                          style: 'currency',
                          currency: plan.currency.toUpperCase()
                        });
                        
                        return (
                          <Grid item xs={12} md={6} key={plan.priceId}>
                            <Fade in={true} timeout={800 + index * 200}>
                              <Card
                                elevation={2}
                                sx={{ 
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  borderRadius: 3,
                                  transition: 'all 0.3s ease',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderColor: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                  <Typography 
                                    variant="h5" 
                                    fontWeight={700} 
                                    gutterBottom
                                    sx={{ 
                                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                      backgroundClip: 'text',
                                      textFillColor: 'transparent',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                    }}
                                  >
                                    {plan.productName}
                                  </Typography>
                                  
                                  <Box sx={{ my: 3 }}>
                                    <Typography 
                                      variant="h3" 
                                      fontWeight={800}
                                      sx={{ 
                                        display: 'inline',
                                        color: theme.palette.primary.main,
                                      }}
                                    >
                                      {price}
                                    </Typography>
                                    <Typography 
                                      variant="h6" 
                                      component="span"
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      / {plan.interval}
                                    </Typography>
                                  </Box>
                                  
                                  {plan.description && (
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                      {plan.description}
                                    </Typography>
                                  )}
                                </CardContent>
                                
                                <CardActions sx={{ p: 3, pt: 0 }}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={() => startCheckout(plan.priceId)}
                                    sx={{
                                      py: 1.5,
                                      fontWeight: 700,
                                      fontSize: '1.1rem',
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                      '&:hover': {
                                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4,
                                      },
                                    }}
                                  >
                                    Subscribe Now
                                  </Button>
                                </CardActions>
                              </Card>
                            </Fade>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ) : (
                  /* New user - no subscription history */
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        fontSize: '2.5rem',
                        mb: 2,
                        display: 'inline-block',
                        opacity: 0.7,
                      }}
                    >
                      🚀
                    </Box>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      Ready to Go VIP?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      Choose a plan below to unlock premium features and exclusive content
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {subscription?.status === 'active' && (
              <Divider sx={{ my: 4 }} />
            )}
            
            {subscription?.status === 'active' && (
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                    Subscription Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Need to make changes to your subscription?
                  </Typography>
                </Box>
                <Button 
                  color="error" 
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelClick}
                  size="large"
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderWidth: 2,
                    minWidth: 200,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
                    },
                  }}
                >
                  Cancel Subscription
                </Button>
              </Box>
            )}
            </>
            ) : (
             <Grid
              container
              spacing={4}
              sx={{
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                alignItems: { xs: 'stretch', md: 'flex-start' },
              }}
             >
              {/* VIP Benefits Section - Left Side */}
              <Grid item xs={12} md={5}>
                <Box sx={{ position: { md: 'sticky' }, top: { md: 100 } }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '20px',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(theme.palette.secondary.main, 0.18)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 22px 48px rgba(32, 74, 163, 0.18)',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <RocketLaunchIcon sx={{ fontSize: 38, color: theme.palette.primary.main }} />
                    </Box>
                    
                    <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 700, color: theme.palette.primary.main, textAlign: 'center' }}>
                      VIP UPGRADE
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
                      {heroTitle}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7, mb: 4, textAlign: 'center' }}
                    >
                      Discover the highest-rated creators based on real subscriber feedback
                    </Typography>
                  </Box>

                  {/* Features List */}
                  <Stack spacing={2}>
                    {[
                      'See top creators ranked by subscriber satisfaction',
                      'View detailed ratings and review counts',
                      'Compare creators side-by-side',
                      'Find the most recommended creators instantly',
                    ].map(feature => (
                      <Stack
                        key={feature}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 8,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main,
                          }}
                        />
                        <Typography variant="body1" fontWeight={500}>
                          {feature}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>

              {/* Plans Section - Right Side */}
               <Grid item xs={12} md={7} sx={{ flexGrow: 1 }}>
                <Box ref={plansSectionRef}>
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    fontWeight={700} 
                    gutterBottom
                    sx={{ mb: 1 }}
                  >
                    Available Plans
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Choose the plan that best fits your needs
                  </Typography>
                  
                  {/* Billing Interval Toggle */}
                  <Box 
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <ToggleButtonGroup
                      value={billingInterval}
                      exclusive
                      onChange={(e, newInterval) => {
                        if (newInterval !== null) {
                          setBillingInterval(newInterval);
                        }
                      }}
                      aria-label="billing interval"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '& .MuiToggleButton-root': {
                          px: 4,
                          py: 1.5,
                          border: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          textTransform: 'none',
                          color: theme.palette.text.secondary,
                          transition: 'all 0.3s ease',
                          '&.Mui-selected': {
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            color: 'white',
                            '&:hover': {
                              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                            },
                          },
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        },
                      }}
                    >
                      <ToggleButton value="month" aria-label="monthly billing">
                        Monthly
                      </ToggleButton>
                      {yearlyAvailable && (
                      <ToggleButton 
                        value="year" 
                        aria-label="yearly billing"
                        sx={{ position: 'relative' }}
                      >
                        Yearly
                        {savingsPercent > 0 && (
                          <Chip
                            label={`Save ${savingsPercent}%`}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -10,
                              right: -10,
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                              color: 'white',
                              border: `1px solid ${theme.palette.success.light}`,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        )}
                      </ToggleButton>
                      )}
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                {/* Plans Cards */}
                <Grid container spacing={3} justifyContent="center">
                  {filteredPlans.map((plan, index) => {
                    const price = (plan.unitAmount / 100).toLocaleString('en-US', {
                      style: 'currency',
                      currency: plan.currency.toUpperCase()
                    });
                    
                    const isYearly = plan.interval === 'year';
                    
                    return (
                      <Grid item xs={12} key={plan.priceId}>
                        <Fade in={true} timeout={800 + index * 200}>
                          <Card
                            elevation={2}
                            sx={{ 
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'visible',
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              background: theme.palette.background.paper,
                              mt: 2,
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 8,
                                borderColor: theme.palette.primary.main,
                              },
                            }}
                          >
                            {isYearly && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -16,
                                  right: 24,
                                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                  color: 'white',
                                  px: 2.5,
                                  py: 0.75,
                                  borderRadius: 2,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  textTransform: 'uppercase',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                  zIndex: 10,
                                }}
                              >
                                BEST VALUE
                              </Box>
                            )}
                            
                            <CardContent sx={{ flexGrow: 1, p: 4 }}>
                              <Typography 
                                variant="h5" 
                                gutterBottom
                                fontWeight={700}
                                sx={{ mb: 1 }}
                              >
                                {plan.productName}
                              </Typography>
                              
                              <Box sx={{ 
                                mb: 3, 
                                p: 3, 
                                borderRadius: 3, 
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                textAlign: 'center'
                              }}>
                                <Typography 
                                  variant="h3" 
                                  component="div"
                                  fontWeight={800}
                                  sx={{ 
                                    color: theme.palette.primary.main,
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {price}
                                  <Typography 
                                    component="span" 
                                    variant="h6" 
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    / {plan.interval}
                                  </Typography>
                                </Typography>
                                {isYearly && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Yearly VIP costs less than 2 creator tips each month
                                  </Typography>
                                )}
                              </Box>
                            </CardContent>
                            
                            <CardActions sx={{ p: 4, pt: 0 }}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={() => startCheckout(plan.priceId)}
                                sx={{ 
                                  py: 1.5,
                                  borderRadius: 2,
                                  fontWeight: 700,
                                  fontSize: '1rem',
                                  textTransform: 'none',
                                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                  boxShadow: isYearly ? '0 4px 20px rgba(30, 136, 229, 0.4)' : undefined,
                                  '&:hover': {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                Subscribe
                              </Button>
                            </CardActions>
                          </Card>
                        </Fade>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              </Grid>
            </Grid>
          )}
          </Paper>
        </Fade>

        {/* Payment Method Selection - Only show if direct payment is enabled */}
        {enableDirectPayment && (
          <Fade in={true} timeout={700}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 1 }}>
                Payment Method
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose how you'd like to process your payment
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    flex: 1,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: paymentMethod === 'redirect' 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    backgroundColor: paymentMethod === 'redirect' 
                      ? alpha(theme.palette.primary.main, 0.05)
                      : theme.palette.background.paper,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                  onClick={() => setPaymentMethod('redirect')}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mx: 'auto',
                        mb: 2,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    >
                      <CreditCardIcon 
                        fontSize="large" 
                        sx={{ color: theme.palette.primary.main }} 
                      />
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Stripe Checkout
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Secure checkout hosted by Stripe
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card 
                  variant="outlined"
                  sx={{ 
                    flex: 1,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: paymentMethod === 'direct' 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    backgroundColor: paymentMethod === 'direct' 
                      ? alpha(theme.palette.primary.main, 0.05)
                      : theme.palette.background.paper,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                  onClick={() => setPaymentMethod('direct')}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mx: 'auto',
                        mb: 2,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    >
                      <PaymentIcon 
                        fontSize="large" 
                        sx={{ color: theme.palette.primary.main }} 
                      />
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Direct Payment
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pay directly through our platform
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </Fade>
        )}

      </Stack>
      
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Button 
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{ 
            py: 1.5,
            px: 4,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          Back
        </Button>
      </Box>
      
      {/* Cancellation Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle 
          id="cancel-dialog-title"
          sx={{ 
            pb: 2,
            textAlign: 'center',
            fontWeight: 700,
            color: 'error.main'
          }}
        >
          Cancel Subscription?
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="cancel-dialog-description"
            sx={{ 
              textAlign: 'center',
              fontSize: '1rem',
              lineHeight: 1.6,
              color: 'text.primary'
            }}
          >
            Are you sure you want to cancel your subscription? Your plan will remain active until{' '}
            <strong>
              {subscription?.current_period_end ? 
                new Date(subscription.current_period_end * 1000).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long', 
                  day: 'numeric'
                }) : 
                'the end of your billing period'
              }
            </strong>, after which you'll lose access to all premium features.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between', gap: 2 }}>
          <Button
            onClick={handleCancelDialogClose}
            variant="outlined"
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={cancellingSubscription}
            color="error"
            variant="contained"
            startIcon={cancellingSubscription ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {cancellingSubscription ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
